import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Input, TextArea } from '../ui/Input';
import { SegmentedControl } from '../ui';
import { taskAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { showSuccess, showError } from '../../utils/toast';
import { quickTaskDraftStore } from '../../utils/quickTaskDraftStore';

interface QuickCreateTaskProps {
  isOpen: boolean;
  onClose: () => void;
}

type Priority = 'low' | 'medium' | 'high';

const PRIORITIES: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export function QuickCreateTask({ isOpen, onClose }: QuickCreateTaskProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setTitle('');
    setDetails('');
    setPriority('medium');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return;
    if (!organization) {
      showError('No organization selected');
      return;
    }

    setSubmitting(true);
    try {
      await taskAPI.create({
        organizationId: String(organization.id),
        title: title.trim(),
        details: details.trim() || undefined,
        priority,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      showSuccess('Task created');
      resetForm();
      onClose();
    } catch (_err) {
      showError('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  }, [title, details, priority, organization, queryClient, resetForm, onClose]);

  const handleMoreOptions = useCallback(() => {
    // Only stash if the user typed something — an empty-title draft would
    // overwrite the org default priority on the full page with our local
    // 'medium' default for no good reason.
    if (user?.id && title.trim()) {
      quickTaskDraftStore.set(String(user.id), { title, details, priority });
    }
    resetForm();
    onClose();
    navigate('/tasks/create');
  }, [user, title, details, priority, resetForm, onClose, navigate]);

  return (
    <Sheet open={isOpen} onDismiss={handleClose} title="Quick Task">
      <div className="flex flex-col gap-4">
        <Input
          label="Title"
          placeholder="What needs to be done?"
          value={title}
          onChange={e => setTitle(e.target.value)}
          autoFocus
          required
        />

        <TextArea
          label="Details (optional)"
          placeholder="Add any details..."
          value={details}
          onChange={e => setDetails(e.target.value)}
          rows={3}
        />

        {/* Priority picker */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-secondary">Priority</span>
          <SegmentedControl<Priority>
            aria-label="Priority"
            options={PRIORITIES}
            value={priority}
            onChange={setPriority}
            tone="pillar"
            pillarColor="aegean"
            className="w-full"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            fullWidth
            loading={submitting}
            disabled={!title.trim() || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Assigning...' : 'Assign task'}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            disabled={submitting}
            onClick={handleMoreOptions}
          >
            More options
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
